import { ref } from 'vue';
import { useInboxStore } from '@dashboardApp/stores/InboxStore';
import { useUserStore } from '@dashboardApp/stores/UserStore';
import useNotifications from '@dashboardApp/composables/useNotifications';
import { performAnalyticsTracking } from '@dashboardApp/helpers/performAnalyticsTracking';
import { api } from '@dashboardApp/store';
import { useDirectoryOnboardingStore } from '@dashboardApp/stores/DirectoryOnboardingStore';
import { tooltipAction } from '@dashboardApp/helpers/tooltip';
import useMemberMeta from '@dashboardApp/composables/useMemberMeta';

let throttleStatus = null;

/**
 * Workaroud to get the response header specifically the throttle status 
 * as pinia-jsonapi does only return the response data
 * 
 * @see DashboardAppMessagesController::reply
 */
api.interceptors.response.use((response) => {
    throttleStatus = response.headers['throttle-status'] || null;
    return response; 
});

export default function useComposeMessage () {
    const { saveStep: saveOnboardingStep } = useDirectoryOnboardingStore();
    const { toggle: toggleAppTooltip } = tooltipAction();
    const { track: trackEvent } = performAnalyticsTracking();
    const inboxStore = useInboxStore();
    const userStore = useUserStore();
    const { addNotification } = useNotifications();
    const { getMeta } = useMemberMeta();

    const content = ref('');
    const subject = ref('');
    const signature = ref(userStore.user?.signature?.signature || '');
    const selectedContent = ref('');
    const showSendButton = ref(true);
    const template = ref('');
    const validateTemplate = ref(false);

    /**
     * Reply a message
     */
    function send(to, name, subj, meta = { source: 'inbox', template : '', products : {}, supplierSlug: '' }) {
        if (validateTemplate.value && content.value === selectedContent.value) {
            addNotification({ message: 'Please customize your message for best results and faster responses', type: 'error' });
            return;
        }

        let message = content.value;
        if (content.value && signature.value) {
            message += `\n\n${signature.value}`
        }

        return inboxStore.sendMessage(Object.assign({
            thread_id: inboxStore.threadId,
            to,
            subject: subject.value || subj,
            content: message,
        }, meta)).then((data) => {
            content.value = '';
            subject.value = '';

            // Add message at the end of the thread instead of reloading/fetching it entirely
            inboxStore.thread.push(data);

            if (!inboxStore.threadId && data?._jv?.id) {
                inboxStore.threadId = data._jv.id;
            }

            addNotification({
                message: `Message sent to ${name}`,
                type: 'success'
            });
            trackSupplierContact(meta);
            trackThrottleStatus(throttleStatus);

            onboardingSteps();
            reset();

            return data;
        }).catch((errs) => {
            trackUnableToSendThrottleStatus(errs.response?.headers['throttle-status'] || null);
            if (errs.response) {
                // API error
                for (let err of errs.response.data.errors) {
                    addNotification({ message: err.detail, type: 'error' });
                }
            } else {
                if (process.env.NODE_ENV === 'development') {
                    console.log(errs)
                }
            }
            return errs;
        });
    }

    function reset() {
        content.value = '';
        subject.value = '';
        selectedContent.value = '';
        showSendButton.value = true;
        validateTemplate.value = false;
        template.value = '';
    }

    function saveSignature() {
        return inboxStore.saveSignature(
            signature.value, userStore.user?.signature?.id
        );
    }

    function trackThrottleStatus(status) {
        if (status && status !== 'normal' && status !== userStore.user.throttleStatus) {
            let ga_event, cio_event;
            switch (status) {
                case 'throttled':
                    ga_event = 'Member Emails Suspended';
                    cio_event = 'EmailMemberSuspended';
                    break;
                case 'danger':
                    ga_event = 'Shown Second Warning';
                    cio_event = 'EmailSecondWarning';
                    break;
                case 'warning':
                    ga_event = 'Shown First Warning';
                    cio_event = 'EmailFirstWarning';
                    break;
                default:
                    ga_event = false;
                    cio_event = false;
            }
            if (ga_event) {
                trackEvent('dl.supplierContactWarning', {
                    'eventAction': ga_event
                });
            }
            if (cio_event) {
                trackEvent(cio_event);
            }
            userStore.user.throttleStatus = status;
        }
    }

    function trackUnableToSendThrottleStatus(status) {
        if (status !== userStore.user.throttleStatus) {
            trackEvent('dl.supplierContactWarning', {
                'eventAction': 'Member Emails Suspended'
            });
            trackEvent('EmailMemberSuspended');
        }
    }

    function trackSupplierContact(meta) {
        if (!meta.supplierSlug) {
            return;
        }

        const attr = {
            contactMethod: meta.source,
            supplierId: meta.supplierSlug,
            imagesAddedToMessage: meta.products.length > 0 ? 1 : 0
        };
        trackEvent('dl.contactSupplier', attr);
        trackEvent('contactSupplier', attr);
    }

    function onboardingSteps() {
        saveOnboardingStep('directory_onboarding_contacted_supplier');
        if (!getMeta('dashboard_inbox_first_message_sent')) {
            userStore.bumpSession().then(() => {
                userStore.fill();
                toggleAppTooltip('inbox_first_message_sent');
            });
        }
    }

    return { 
        content, 
        subject, 
        signature,
        selectedContent, 
        showSendButton, 
        validateTemplate, 
        template,
        send, 
        reset,
        saveSignature
    }
}